import React, { useEffect, useState } from 'react';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';

/**
 * The "+N" count chip and its hover list, shared by the landing-page list and
 * the Linked Records table. The popup mounts on the body because antd measures
 * its auto-flip against the container, and a table cell is never tall enough.
 */

const COUNT_COLOR = '#0071CD';
const REMAINING_COLOR = '#DFE2E7';
const TOOLTIP_BACKGROUND_COLOR = '#232630';

const CHARS_PER_LINE = 28;
const MAX_LINES_PER_NAME = 2;
export const DEFAULT_MAX_LINES = 15;

export interface NameListEntry {
  id?: string;
  name?: string;
}

const CountBadge: React.FC<{
  count: number;
  tooltipOpen: boolean;
  isSelected: boolean;
  onOpen: () => void;
  onClose: () => void;
}> = ({ count, tooltipOpen, isSelected, onOpen, onClose }) => {
  const [hovered, setHovered] = useState(false);
  const highlighted = hovered || tooltipOpen;
  const highlightColor = isSelected ? '#AADAFF' : '#DFE2E7';

  return (
    <button
      type="button"
      className="text-sm font-normal shrink-0 focus:outline-hidden focus:shadow-outline border-none bg-transparent p-0 cursor-pointer"
      style={{
        color: COUNT_COLOR,
        borderRadius: 2,
        padding: 4,
        margin: -4,
        background: highlighted ? highlightColor : undefined
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => {
        setHovered(false);
        onClose();
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        e.stopPropagation();
      }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (tooltipOpen) onClose();
        else onOpen();
      }}
    >
      +{count}
    </button>
  );
};

/**
 * `maxLines` caps rendered height (a long name costs two); `maxItems` caps the
 * number of names. Spending an item cap as a line budget shows too few.
 */
export const buildNameList = (
  names: NameListEntry[],
  total: number,
  maxLines: number,
  maxItems?: number
): { shown: string[]; remaining: number } => {
  let usedLines = 0;
  const shown: string[] = [];
  let remaining = 0;

  for (let i = 0; i < names.length; i += 1) {
    const name = names[i]?.name || '';
    const lines = Math.min(
      Math.ceil(name.length / CHARS_PER_LINE) || 1,
      MAX_LINES_PER_NAME
    );

    const withinItems = maxItems === undefined || shown.length < maxItems;

    if (withinItems && usedLines + lines <= maxLines) {
      shown.push(name);
      usedLines += lines;
    } else {
      remaining = total - i;
      break;
    }
  }

  if (remaining === 0 && total > shown.length) {
    remaining = total - shown.length;
  }

  return { shown, remaining };
};

interface NameListTooltipProps {
  title: string;
  names: NameListEntry[];
  total: number;
  maxLines?: number;
  maxItems?: number;
  isSelected?: boolean;
  /** The side it opens on, not a preference — antd only flips on overflow. */
  placement?: 'topRight' | 'bottomRight';
  nameWrap?: 'characters' | 'words';
}

const NameListTooltip: React.FC<NameListTooltipProps> = ({
  title,
  names,
  total,
  maxLines = DEFAULT_MAX_LINES,
  maxItems,
  isSelected = false,
  placement = 'topRight',
  nameWrap = 'characters'
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Scroll doesn't bubble, hence capture phase.
  useEffect(() => {
    if (!tooltipOpen) return undefined;
    const closeOnScroll = () => setTooltipOpen(false);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, [tooltipOpen]);

  const additionalCount = Math.max(0, total - 1);
  if (additionalCount <= 0) return null;

  const badge = (
    <CountBadge
      count={additionalCount}
      tooltipOpen={tooltipOpen}
      isSelected={isSelected}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
    />
  );

  if (names.length <= 1) return badge;

  const { shown, remaining } = buildNameList(
    names,
    total,
    maxItems === undefined ? maxLines : maxItems * MAX_LINES_PER_NAME,
    maxItems
  );

  const content = (
    <div className="text-left p-2" data-testid="name-list-tooltip">
      <div className="font-semibold mb-2">{title}</div>
      <ul className="list-disc pl-4 space-y-1 ml-1">
        {/* Keyed by position: a record can carry the same product twice, and
            the API returns the same name *and* id for both. */}
        {shown.map((name, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={`${index}-${name}`} className="text-sm">
            <div
              style={{
                lineHeight: '1.4',
                display: '-webkit-box',
                WebkitLineClamp: MAX_LINES_PER_NAME,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                ...(nameWrap === 'words'
                  ? { overflowWrap: 'anywhere' as const }
                  : { wordBreak: 'break-all' as const })
              }}
            >
              {name}
            </div>
          </li>
        ))}
      </ul>
      {remaining > 0 && (
        <div
          className="mt-2 text-sm font-normal"
          style={{ color: REMAINING_COLOR }}
        >
          + {remaining} more
        </div>
      )}
    </div>
  );

  return (
    <Tooltip
      title={content}
      placement={placement}
      arrowPointAtCenter
      align={{ offset: [24, 0] }}
      color={TOOLTIP_BACKGROUND_COLOR}
      open={tooltipOpen}
      onOpenChange={setTooltipOpen}
      getPopupContainer={() => document.body}
    >
      <span className="inline-flex">{badge}</span>
    </Tooltip>
  );
};

export default NameListTooltip;
